import React, { useState, useEffect } from "react";
import { Grid, Typography, Button, Paper, Box, FormControl, TextField, IconButton, Switch } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clearMessage } from "../../redux/Slice/message";
import { createB2bLead } from "../../redux/Slice/b2bLeadSlice";
import { setLoading } from "../../redux/Slice/loaderSlice";
import Editor from "../../components/EditorTemplate/Editor";
import FormFields from "../../components/EditorTemplate/FormFields";
import SaveDialog from "../../components/EditorTemplate/HelperDialog/SaveDialog";
import { AddCircle, Check, Edit, RemoveCircle } from '@mui/icons-material';

const CreateB2BLead = () => {
  const [templateName, setTemplateName] = useState("");
  const [inputSchema, setInputSchema] = useState({});
  const [contactSchema, setContactSchema] = useState([]);
  const [outputSchema, setOutputSchema] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const message = useSelector((state) => state.message);
  

  useEffect(() => {
    if (message?.message) {
      toast(message.message);
      dispatch(clearMessage());
    }
  }, [message, dispatch]);

  const handleSave = () => {
    dispatch(setLoading(true));
    const formData = { 
      templateName, 
      inputJsonSchema: inputSchema,
      contactJsonSchema: contactSchema,
      outpuJsonSchema: outputSchema 
    };

    dispatch(createB2bLead(formData))
      .then(() => {
        dispatch(setLoading(false));
        navigate("/leadtemplate");
      })
      .catch(() => dispatch(setLoading(false)));

    setIsDialogOpen(false);
  };

  const addArrayItem = (path, setSchema) => {
    setSchema((prevState) => {
        const keys = path.split(".");
        const newState = { ...prevState };
        let current = newState;

        keys.forEach((key, index) => {
            if (!current[key]) {
                current[key] = isNaN(keys[index + 1]) ? {} : [];
            }
            if (index === keys.length - 1) {
                current[key] = [...(current[key] || []), {}];
            } else {
                current[key] = { ...current[key] };
                current = current[key];
            }
        });

        return newState;
    });
};

const removeArrayItem = (path, index, setSchema) => {
    setSchema((prevState) => {
        const keys = path.split(".");
        const newState = { ...prevState };
        let current = newState;

        keys.forEach((key, idx) => {
            if (idx === keys.length - 1 && Array.isArray(current[key])) {
                current[key].splice(index, 1);
            } else {
                current[key] = { ...current[key] };
                current = current[key];
            }
        });

        return newState;
    });
};


const handleChange = (path, value, setSchema) => {
    const keys = path.split(".");
    setSchema((prevState) => {
        const newState = { ...prevState };
        let current = newState;

        keys.forEach((key, index) => {
            if (index === keys.length - 1) {
                current[key] = value;
            } else {
                current[key] = { ...current[key] };
                current = current[key];
            }
        });

        return newState;
    });
};

const getValue = (path, schema) =>
    path.split(".").reduce((acc, key) => acc?.[key], schema);

const handleAddContactSchema = () => {
    setContactSchema((prev) => [...prev, {}]);
};

const handleRemoveContactSchema = (index) => {
    setContactSchema((prev) => prev.filter((_, i) => i !== index));
};

const handleSchemaChange = (index, updatedSchema) => {
    setContactSchema((prev) => prev.map((schema, i) => (i === index ? updatedSchema : schema)));
};

const handleToggleContactSchema = (index, status) => {
  const updatedSchema = [...contactSchema];
  updatedSchema[index].status = status;
  setContactSchema(updatedSchema);
}

const handleTitleChange = (index, newTitle) => {
  const updatedSchema = [...contactSchema];
  updatedSchema[index].title = newTitle;
  setContactSchema(updatedSchema)
}

const handleEditClick = (index) => {
  const updatedSchema = [...contactSchema];
  updatedSchema[index].isEditable = true;
  setContactSchema(updatedSchema);
};

const handleSaveClick = (index) => {
  const updatedSchema = [...contactSchema];
  updatedSchema[index].isEditable = false;
  setContactSchema(updatedSchema);
};

return (
    <>
     <Typography variant='h4' gutterBottom marginBottom={4}>B2B Lead Details</Typography>
     <Grid  container spacing={2} sx={{ padding: 3, border: "1px solid #ddd", borderRadius: 2 }}>
    
        {/* Input Schema */}
        <Grid container spacing={4} sx={{ padding: 3 }}>
            <Grid item xs={12} md={6}>
                
                <Paper elevation={3} sx={{ padding: 3, marginBottom: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                        <Typography variant='h6'>Input Schema</Typography>
                    </Box>
                    <Editor schema={inputSchema} onChange={(value) => setInputSchema(value)} />
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ padding: 3}}>
                    <Typography variant="h6" marginBottom={2}>
                        Define Key-Value Pairs
                    </Typography>
                    <FormFields
                        schema={inputSchema}
                        getValue={(path) => getValue(path, inputSchema)}
                        handleChange={(path, value) => handleChange(path, value, setInputSchema)}
                        addArrayItem={(path) => addArrayItem(path, setInputSchema)}
                        removeArrayItem={(path, index) => removeArrayItem(path, index, setInputSchema)}
                    />
                    
                </Paper>
            </Grid>
        </Grid>

        {/* Contact Schema */}
        <Grid container spacing={4} sx={{padding:3}}>
          <Grid item xs={12} md={6}>
            {contactSchema.map((schema, index) => (
              <Paper key={index} elevation={3} sx={{ padding:3, marginBottom: 2}}>
              <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                {/* Editable Title with TextField */}
                <Grid item size={{xs:12, md:2}}>
                 <FormControl fullWidth>
                 <Box display="flex" alignItems="center" sx={{ gap: 2 }}>
                 {schema.isEditable ? (
                  <TextField  variant="outlined" value={schema.title } // Fallback title
                  onChange={(e) => handleTitleChange(index, e.target.value)} // Handler to update title
                  sx={{ flexGrow: 1 }}
                 />
                 ): (
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {schema.title || `Contact Schema ${index + 1}`} {/* Fallback title */}
                  </Typography>
                )} 
                {/* Edit/Save Icon Button */}
                {schema.isEditable ? (
                  <IconButton color='primary' onClick={() => handleSaveClick(index)} sx={{ fontSize: 18 }} >
                    <Check color='success' sx={{ fontSize: 'inherit' }}/>
                  </IconButton>
                 ):(
                  <IconButton color='primary' onClick={() => handleEditClick(index)} sx={{ fontSize: 18 }}>
                    <Edit sx={{ fontSize: 'inherit' }}/>
                  </IconButton>
                 )}
                 </Box>
                 </FormControl>
                 
                </Grid>
                {/* Toggle Switch */}
                <Switch color='primary' checked={schema.status}
                onChange={(e) => handleToggleContactSchema(index, e.target.checked)}/>
                <IconButton color='error' onClick={() => handleRemoveContactSchema(index)}>
                  <RemoveCircle/>
                </IconButton>
              </Box>
               <Editor schema={schema} onChange={(updatedSchema) => handleSchemaChange(index, updatedSchema)} />
              </Paper>
            ))}
            <Button variant='contained' color='success' startIcon={<AddCircle/>} onClick={handleAddContactSchema}>Add Contact Schema</Button>
              
          </Grid>

          <Grid item xs={12} md={6}>
          {contactSchema.map((schema, index) => (
                <Paper key={index} elevation={3} sx={{ padding: 3, marginBottom: 2}}>
                  <Typography variant="h6" marginBottom={2}>
                    Define Key-Value Pairs
                  </Typography>
                  <FormFields
                    schema={schema}
                    getValue={(path) => getValue(path, contactSchema)}
                    handleChange={(path, value) => handleChange(path, value, setContactSchema)}
                    addArrayItem={(path) => addArrayItem(path, setContactSchema)}
                    removeArrayItem={(path, index) => removeArrayItem(path, index, setContactSchema)}
                  />
                </Paper>
              ))}
              </Grid>
          </Grid>

        {/* Output Schema */}
        <Grid container spacing={4} sx={{ padding: 3 }}>
            <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ padding: 3, marginBottom: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                        <Typography variant='h6'>Output Schema</Typography>
                    </Box>
                    <Editor schema={outputSchema} onChange={(value) => setOutputSchema(value)} />
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ padding: 3 }}>
                    <Typography variant="h6" marginBottom={2}>
                        Define Key-Value Pairs
                    </Typography>
                    <FormFields
                        schema={outputSchema}
                        getValue={(path) => getValue(path, outputSchema)}
                        handleChange={(path, value) => handleChange(path, value, setOutputSchema)}
                        addArrayItem={(path) => addArrayItem(path, setOutputSchema)}
                        removeArrayItem={(path, index) => removeArrayItem(path, index, setOutputSchema)}
                    />
                </Paper>
            </Grid>
        </Grid>
        <Grid  sx={{ justifyContent: "flex-end", display: "flex", gap: 3, mt: 3 ,width: "100%"}}>
        <Button variant="contained" color="primary" onClick={() => setIsDialogOpen(true)} >
          Save Quote
        </Button>
        </Grid>
        </Grid>

        <SaveDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            templateName={templateName}
            onTemplateNameChange={setTemplateName}
            onSave={handleSave}
        />
        
    </>
);
};

export default CreateB2BLead;
